import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import SearchInput from 'assets/components/inputs/search/SearchInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import { compact, filter, flatten, sortBy, uniqBy } from 'lodash';
import Permission from 'models/core/permission/Model.Permission';
import callApiRoleSave from 'models/core/role/save/Api.Role.Save';
import { useMemo, useState } from 'react';

export default function RoleEdit(
  props: Template.ModalForm.MinimalImport<
    Model.IRole,
    Api.Core.Role.Save.IRequest,
    Api.Res<Api.Core.Role.Save.IResponse>
  >
) {
  const { lang } = getLocales();
  const save = useApi({ action: callApiRoleSave, callback: props.onSave, wait: true });
  const permissionsApi = useApi({ action: Permission.list });

  const [showSelectedPermissionsOnly, setShowSelectedPermissionsOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<number>();
  const [appFilter, setAppFilter] = useState<number>();
  const [permissionQuery, setPermissionQuery] = useState('');
  const permissions = useMemo(() => {
    function validateQuery(it: Permission) {
      return new RegExp(permissionQuery.toLowerCase()).test((it.name + ' ' + it.description + ' ').toLowerCase());
    }
    return sortBy(
      filter(
        compact(permissionsApi?.payload?.data),
        (it) =>
          (!permissionQuery ? true : validateQuery(it)) &&
          (!appFilter ? true : it.application.id === appFilter) &&
          (!categoryFilter ? true : it.category.id === categoryFilter)
      ),
      (it) => it.application.name,
      (it) => it.name
    );
  }, [permissionsApi?.payload?.data, permissionQuery, appFilter, categoryFilter]);

  const filterApps = useMemo(
    () =>
      sortBy(
        uniqBy(compact(flatten(permissionsApi.payload?.data?.map((it) => it.application))), (it) => it.id),
        (it) => it.name
      ),
    [permissionsApi.payload?.data]
  );
  const filterCategories = useMemo(
    () =>
      sortBy(
        uniqBy(compact(flatten(permissionsApi.payload?.data?.map((it) => it.category))), (it) => it.id),
        (it) => it.name
      ),
    [permissionsApi.payload?.data]
  );

  return (
    <>
      <ModalForm<Api.Core.Role.Save.IRequest>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[props.data]}
        title={lang.role}
        default={{
          ...props.data,
          translations: props.data?.translations || [],
          permissions: props.data?.permissions || [],
        }}
        onCancel={props.onCancel}
        onSubmit={(data) => save.execute(data)}
        validation={(data, errors) => {
          if (!data.translations?.find((it) => it.languageIsoCode === Language.english)?.name)
            errors.name = lang.mustNotBeEmpty;
          if (!data.permissions?.length) errors.permissions = { _objectError: lang.mustSelectAtleastOneOption };
        }}
        beforeUpdate={(data, updates, activeTab) => {
          if (updates.name || updates.description) {
            if (!data.translations) data.translations = [];
            const translation = data.translations?.find((it) => it.languageIsoCode == activeTab.id);
            if (translation && updates.name) translation.name = data.name;
            else if (translation && updates.description) translation.description = data.description;
            else
              data.translations.push({ name: data.name, description: data.description, languageIsoCode: activeTab.id });
            delete data.name;
            delete data.description;
            return data;
          } else return data;
        }}
        tabs={[
          { id: Language.english, text: lang.en, icon: 'fas-globe' },
          { id: Language.french, text: lang.fr, icon: 'fas-globe' },
          { id: 'permissions', icon: 'fas-shield-alt', text: lang.permissions },
        ]}
        render={({ form, activeTab }) => {
          const permissionList = showSelectedPermissionsOnly
            ? permissions?.filter((permission) => form.data.permissions?.find((it) => it.id === permission.id))
            : permissions;

          const areAllSelected = form.data.permissions?.length === permissionList.length;
          const areNoneSelected = form.data.permissions?.length === 0;
          return (
            <>
              {activeTab?.id !== 'permissions' && (
                <FormWrapper>
                  <TextInput<Utils.ReadonlyKeys<Model.IRole>>
                    name="name"
                    label={lang.name}
                    value={form.data.translations?.find((it) => it.languageIsoCode === activeTab.id)?.name}
                    onChange={form.update}
                    error={activeTab.id === Language.english ? form.errors?.name : null}
                    icon={<Icon class="if-quill-pen" />}
                  />
                  <MultilineTextInput
                    name="description"
                    label={lang.description}
                    error={form.errors?.description}
                    value={form.data.translations?.find((it) => it.languageIsoCode === activeTab.id)?.description}
                    onChange={form.update}
                    icon={<Icon class="if-quill-pen" />}
                  />
                </FormWrapper>
              )}
              {activeTab?.id == 'permissions' && (
                <table className={ThemeTableSimple}>
                  <thead>
                    <tr>
                      <th>
                        <Button
                          class={{ button: ThemeButtonCircle }}
                          media="fas-tasks"
                          onClick={() => setShowSelectedPermissionsOnly((it) => !it)}
                          isLoading={permissionsApi.isExecuting}
                          active={showSelectedPermissionsOnly}
                          htmlElementProps={{ title: lang.showOnlySelected }}
                        />
                      </th>
                      <th>
                        <SearchInput onSearch={(q) => setPermissionQuery(q)} />
                      </th>
                      <th>
                        <SelectInput
                          name="filter"
                          placeholder={lang.application}
                          data={filterApps.map((item) => item.displayInfo)}
                          value={appFilter}
                          disabled={!filterApps.length}
                          onChange={({ filter }) => setAppFilter(filter)}
                          style={{ marginTop: 0 }}
                          allowClear
                        />
                      </th>
                      <th>
                        <SelectInput
                          name="filter"
                          placeholder={lang.category}
                          data={filterCategories.map((item) => item.displayInfo)}
                          value={categoryFilter}
                          disabled={!filterCategories.length}
                          onChange={({ filter }) => setCategoryFilter(filter)}
                          style={{ marginTop: 0 }}
                          allowClear
                        />
                      </th>
                    </tr>
                    <tr>
                      <th>
                        <Button
                          class={{ button: ThemeButtonCircle }}
                          media={
                            areAllSelected ? 'fas-check-square' : areNoneSelected ? 'far-square' : 'fas-minus-square'
                          }
                          disabled={!permissionsApi?.payload?.count}
                          onClick={async () => {
                            form.update({
                              permissions: areAllSelected ? [] : compact(permissionList),
                            });
                          }}
                          isLoading={permissionsApi.isExecuting}
                          active={!areNoneSelected}
                          htmlElementProps={{ title: lang.selectAll }}
                        />
                      </th>
                      <th>{lang.permission}</th>
                      <th>{lang.application}</th>
                      <th>{lang.category}</th>
                    </tr>
                  </thead>
                  {permissionList?.length > 0 && (
                    <tbody>
                      <DataMap
                        data={permissionList}
                        render={({ data: permission }) => {
                          const currentPermission = form.data.permissions?.find((it) => it.id === permission.id);

                          return (
                            <tr key={permission.id}>
                              <td>
                                <Button
                                  class={{ button: ThemeButtonCircle }}
                                  htmlElementProps={{
                                    title: currentPermission ? lang.unselect : lang.select,
                                  }}
                                  onClick={() => {
                                    if (currentPermission)
                                      form.update({
                                        permissions: form.data.permissions?.filter(
                                          (it) => it.id !== currentPermission?.id
                                        ),
                                      });
                                    else
                                      form.update({
                                        permissions: [...(form.data.permissions || []), permission],
                                      });
                                  }}
                                  media={currentPermission ? 'fas-check-square' : 'far-square'}
                                />
                              </td>
                              <td>
                                <b>{permission.name}</b>
                                <br />
                                <span>{permission.description}</span>
                              </td>
                              <td>{permission?.application?.name}</td>
                              <td>{permission?.category?.name}</td>
                            </tr>
                          );
                        }}
                      />
                    </tbody>
                  )}
                </table>
              )}
            </>
          );
        }}
      />
    </>
  );
}
